<script setup>
import { onMounted, ref } from 'vue';
import Dialog from 'primevue/dialog';
import Gallery from './Gallery.vue';
import { useI18n } from 'vue-i18n';
import { useDateFormat } from '@vueuse/core';
import { useAuthStore } from '@stores';
import axios from 'axios';
import { usePdfGenerator } from '../../../composables/usePdfGenerator';

const props = defineProps(['item', 'id']);

const { t } = useI18n({ useScope: 'global' });
const { triggerPdfGeneration } = usePdfGenerator();
const apiUrl = import.meta.env.VITE_API_URL;
const authStore = useAuthStore();

const isModalVisible = ref(false);
const result = ref({});

const groupImages = (item) => {
    const groups = {};
    const images = item?.before_exam || [];

    images.forEach((img) => {
        const date = useDateFormat(img.created_at, 'DD.MM.YYYY').value;
        if (!groups[date]) {
            groups[date] = [];
        }
        groups[date].push(img);
    });

    return groups;
};

const getResultData = async () => {
    try {
        const response = await axios.get(`${apiUrl}/v1/file_generate`, {
            params: {
                attempt_id: props.id,
            },
            headers: {
                Authorization: `Bearer ${authStore.userToken}`,
                'Content-Type': 'application/json',
            },
        });
        result.value = response.data.data;
    } catch (error) {
        console.error(error);
    }
};

const handlePdfGeneration = async () => {
    await getResultData();
    triggerPdfGeneration(result.value);
};
</script>

<template>
    <Dialog modal :header="t('global.video')" v-model:visible="isModalVisible">
        <video v-if="item.video?.url" :src="item.video?.url" width="480" height="360" controls></video>
    </Dialog>

    <DataTable :value="item?.cheatings">
        <template #empty> {{ t('global.noData') }}</template>
        <template #header>
            <div class="title-section">
                <span class="text-xl font-bold">{{ t('profile.violation') }}</span>
                <div class="buttons-group" v-if="item?.status === 'FINISHED' || item?.status === 'BLOCKED'">
                    <Button class="button-action" v-if="item?.video.url && !item?.video.is_video_deleted" as="a" :href="item?.video.url" target="_blank" rel="noopener noreferrer" size="small" icon="pi pi-video" :label="`${t('profile.download')} ${(item?.video.size / 1024 / 1024).toFixed(2)} MB`" />

                    <Button class="button-action" v-if="item?.video.url && !item?.video.is_video_deleted" size="small" severity="info" :label="t('profile.openVideo')" icon="pi pi-play-circle" @click="isModalVisible = true" />

                    <p v-else-if="!item?.video.url" class="error-text">{{ t('profile.videoError') }}</p>
                    <p v-else class="error-text">{{ t('profile.videoDeleted') }}</p>

                    <Button class="button-action" size="small" severity="help" :label="t('profile.pdfDownload')" icon="pi pi-file-pdf" @click="handlePdfGeneration" />
                </div>
            </div>
        </template>
        <Column field="code" :header="t('profile.code')">
            <template #body="{ data }">
                {{ data.code.toUpperCase() }}
            </template>
        </Column>
        <Column field="code" :header="t('profile.desc')">
            <template #body="{ data }">
                {{ t(`violations.${data.code}.long`) }}
            </template>
        </Column>
        <Column field="score" :header="t('profile.penalty')">
            <template #body="{ data }">
                <span>{{ data.score }}</span>
            </template>
        </Column>
        <Column field="created_at" :header="t('profile.time')">
            <template #body="{ data }">
                <span>{{ useDateFormat(data.created_at, 'HH:mm:ss').value }}</span>
            </template>
        </Column>
    </DataTable>

    <Htag v-if="item?.before_exam?.length" tag="h1" class="gallery-title"> {{ t('profile.beforeExam') }}: </Htag>

    <div v-for="(images, date) in groupImages(item)" :key="date" class="date-group">
        <Htag tag="h2">{{ date }}</Htag>
        <div class="gallery-wrapper">
            <Gallery v-for="img in images" :key="img.created_at" :id="id + '1'" :src="img.image" :isFaceMathed="img.is_face_matched" :isSpoofed="img.is_spoofed" :created="img.created_at" :original="props.item.originalImg" />
        </div>
    </div>

    <Htag v-if="item?.during_exam?.length" tag="h1" class="gallery-title"> {{ t('profile.duringExam') }}: </Htag>

    <div class="gallery-wrapper">
        <Gallery v-for="img in item?.during_exam" :key="img.created_at" :id="id + '2'" :src="img.image" :isFaceMathed="img.is_face_matched" :isSpoofed="img.is_spoofed" :created="img.created_at" :original="props.item.originalImg" />
    </div>
</template>

<style scoped>
.gallery-wrapper {
    @apply flex flex-wrap gap-4;
}

.gallery-title {
    @apply my-4;
}

.date-group {
    @apply my-4;
}

.error-text {
    @apply text-red-500 text-sm;
}

.buttons-group {
    @apply flex items-center gap-2;
}
.title-section {
    @apply flex flex-wrap items-center justify-between gap-2;
}
@media (max-width: 768px) {
    .buttons-group {
        @apply flex-col gap-2;
    }
    .button-action {
        @apply w-full;
    }
    .title-section {
        @apply flex-col gap-2;
    }
}

@media (max-width: 1024px) {
}
</style>
